import React from 'react'
import { Row } from 'react-bootstrap'
import { observer } from 'mobx-react'
import { subscriptionContainer } from 'src/models'
import {
  deleteFromLocalStorage,
  getLocalStorage,
  getMonthStringList,
  i18nTranslate,
} from 'src/utils'
import { ProductCard } from './ProductCard'
import { SubscriptionFooter } from './SubscriptionFooter'
import {
  canAllowToSkipDeleteChange,
  constructDateV2,
  getTodayDateV2,
  currentMonthV2,
  currentYearV2,
} from 'src/deps'
import './styles.scss'

@observer
class SubscriptionSchedule extends React.Component {
  isCurrentMonth = false
  isFistMonthSkip = false
  componentDidUpdate() {
    this.scrollToSelectedDate()
  }

  scrollToSelectedDate = () => {
    const selectedDate = getLocalStorage('scheduleOrderSelectedDate') || ''
    let scheduleDateTab = document.getElementById(selectedDate)
    if (scheduleDateTab) {
      const scrollBehavior = { behavior: 'smooth', block: 'center' }
      scheduleDateTab?.scrollIntoView(scrollBehavior)
      deleteFromLocalStorage('scheduleOrderSelectedDate')
    }
  }
  /**
   * Renders a product card wrapper component.
   *
   * @param {object} product - The product data to pass to the ProductCard component.
   * @returns {JSX.Element} - The rendered ProductCard component wrapped in a div.
   */
  renderProductWrapper = (
    product,
    index,
    processingDate,
    selectedMonth,
    canChangeFrequencyToOneTime = false,
    canSkipOrDelete = false
  ) => {
    const data = {
      ...product,
      isCurrentMonth: this.isCurrentMonth,
      processingDate,
      selectedMonth,
    }
    return (
      <div key={product.id} className="product-wrapper subs-product-wrapper">
        <div>
          <ProductCard
            data={data}
            canChangeFrequencyToOneTime={canChangeFrequencyToOneTime}
            canSkipOrDelete={canSkipOrDelete}
            scrollToSelectedDate={this.scrollToSelectedDate}
            {...this.props}
          />
        </div>
      </div>
    )
  }

  /**
   * Renders the date for a subscription group.
   *
   * @param {object} group - The subscription group object.
   * @param {number} selectedMonth - The month that is selected.
   * @returns {JSX.Element} - The rendered date element.
   */
  renderGroupDate = (group, selectedMonth) => {
    let currentMonth = currentMonthV2()
    const currentYear = currentYearV2()
    const isNextYearMonth = currentMonth > selectedMonth - 1
    const year = isNextYearMonth ? parseInt(currentYear) + 1 : currentYear
    const monthText = getMonthStringList()[selectedMonth - 1]
    const selectedDate = `${monthText} ${group?.[0]}`
    const isSkipped =
      group[1].isSkipped === 'true' || group[1].isSkipped === true
    return (
      <Row noGutters className="schedule-date-wrapper">
        <h2
          className="date-section"
          data-testid="monthly_date"
          id={selectedDate}>
          {`${selectedDate}, ${year}`}
          {isSkipped && (
            <span className="order-skipped-label">
              {i18nTranslate('subscription.orderSkipped', 'Order Skipped')}
            </span>
          )}
        </h2>
      </Row>
    )
  }

  /**
   * Renders the products for a subscription group for the given month.
   *
   * @param {Object} group - The subscription group object
   * @param {number} selectedMonth - The month to render products for
   * @param {Object[]} subscriptions - The subscription data to render
   * @returns {JSX.Element} - The rendered product cards for the group
   */
  renderGroupProduct = (
    group,
    selectedMonth,
    subscriptions,
    orderSuccessDates = ''
  ) => {
    const { availableOrderDates = [] } = this.props
    const { subscriptionIds = '' } = group?.[1]
    const processingDate = parseInt(group?.[0] || 0)
    if (orderSuccessDates.length > 0 && group?.[0]) {
      let processingDateList = orderSuccessDates.split(',').map(val => {
        return val.trim()
      })
      // const processingDateList = orderSuccessDates.split(',')
      if (processingDateList.includes(group?.[0])) {
        return
      }
    }
    let subscriptionsArray = {}
    const subscriptionListIds = subscriptionIds?.split(',') || []
    subscriptionsArray = subscriptions?.filter(item => {
      const id = item.id
      return subscriptionListIds.includes(id.toString())
    })
    subscriptionsArray?.sort((a, b) =>
      a.type === 'VARIABLE_SINGLE_ORDER' ? 1 : -1
    )
    const currentDate = getTodayDateV2()
    const selectDate = constructDateV2(
      currentDate.getFullYear(),
      selectedMonth - 1,
      parseInt(processingDate)
    )
    this.isCurrentMonth = availableOrderDates?.some(
      item =>
        item?.getDate() === selectDate?.getDate() &&
        item?.getMonth() === selectDate?.getMonth()
    )
    return (
      <div>
        {this.renderGroupDate(group, selectedMonth)}
        {subscriptionsArray?.map((product, index) => {
          const {
            canChangeFrequencyToOneTime = false,
            canSkipOrDelete = false,
          } = canAllowToSkipDeleteChange(subscriptionsArray, product)
          return this.renderProductWrapper(
            product,
            index,
            processingDate,
            selectedMonth,
            canChangeFrequencyToOneTime,
            canSkipOrDelete
          )
        })}
      </div>
    )
  }

  /**
   * Renders subscription groups and their products for the given month.
   *
   * @param {Object} GroupProducts - Object containing subscription group data, selected month, and subscription data
   * @returns {JSX.Element} - Rendered product cards for the subscription groups
   */
  getGroupListData = data => {
    let list = []
    data.forEach(group => {
      list.push(group?.[0] || 0)
    })
    return list
  }
  renderGroup = (GroupProducts, index = 0) => {
    const {
      subscriptionGroup = {},
      selectedMonth = 1,
      subscriptions,
      orderSuccessDates = '',
    } = GroupProducts
    const processingDateList = orderSuccessDates?.split?.(',')?.map(val => {
      return val?.trim()
    })
    const subscriptionGroupList = Object.entries(subscriptionGroup)
    const dataList = this.getGroupListData(subscriptionGroupList)
    let res = dataList?.filter(item => {
      return !processingDateList?.includes(item)
    })
    return (
      <div>
        {subscriptionGroupList?.length > 0 &&
          subscriptionGroupList?.map(group => {
            return this.renderGroupProduct(
              group,
              selectedMonth,
              subscriptions,
              orderSuccessDates
            )
          })}
      </div>
    )
  }
  getSubscriptionResponseCheck = subscriptionsResponse => {
    const {
      subscriptionGroup = {},
      selectedMonth = 1,
      subscriptions = {},
      orderSuccessDates = '',
    } = subscriptionsResponse?.[0] || {}
    const subscriptionGroupList = Object.entries(subscriptionGroup)
    const dataList = this.getGroupListData(subscriptionGroupList)
    const processingDateList = orderSuccessDates?.split(',').map(val => {
      return val.trim()
    })
    let res = dataList.filter(item => {
      return !processingDateList.includes(item)
    })
    const currentMonth = new Date().getMonth() + 1
    if (currentMonth === selectedMonth && res.length === 0) {
      this.isFistMonthSkip = true
    } else if (currentMonth === selectedMonth && res.length > 0) {
      this.isFistMonthSkip = false
    }
  }
  render() {
    let res = subscriptionContainer.subscriptionPreferenceResponse
      ?.selectedSubscriptions
      ? [
          ...subscriptionContainer.subscriptionPreferenceResponse
            ?.selectedSubscriptions,
        ]
      : []
    res?.length > 0 && this.getSubscriptionResponseCheck(res)
    if (!this.isFistMonthSkip && res?.length > 4) {
      res = res.splice(0, 4)
    }
    return (
      <>
        {/* <div className="subscription-next-order-search-section mt-3">
          <SubscriptionInfiniteScrollV2 addButtonType="plus" {...this.props} />
        </div> */}
        {res.length &&
          res?.map((GroupProducts, index) => {
            return this.renderGroup(GroupProducts, index)
          })}
        <SubscriptionFooter {...this.props} />
      </>
    )
  }
}

export { SubscriptionSchedule }
export default SubscriptionSchedule
